import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
    Line,
    LineChart,
    ReferenceLine,
    Tooltip,
    XAxis,
    YAxis,
    CartesianGrid,
} from "recharts";
import { colors } from "../../theme/colors";
import ClientResponsiveContainer from "./ClientResponsiveContainer";
import { TooltipShell } from "../common/Tooltip";
import ProfileMatchTooltipHeader, {
    TOOLTIP_ROW_SX,
} from "./ProfileMatchTooltipHeader";
import {
    buildGameAxisTicks,
    buildRating3TrendSeries,
} from "./profileRecentMatches";
import { ProfileChartSkeleton } from "./profileSkeletons";

const buildRatingTrendStats = (avgRating, points) => [
    {
        label: "Avg",
        value: avgRating?.toFixed(2),
        color: colors.primary.main,
        glow: alpha(colors.primary.main, 0.18),
    },
    {
        label: "Lowest",
        value: Math.min(...points.map((p) => p.rating)).toFixed(2),
        color: colors.text.primary,
        glow: "rgba(255,255,255,0.08)",
    },
    {
        label: "Peak",
        value: Math.max(...points.map((p) => p.rating)).toFixed(2),
        color: "#F6B94C",
        glow: "rgba(246,185,76,0.16)",
    },
];

function RatingTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;

    const ratingColor =
        d.rating < 1 ? colors.state.danger.main : colors.state.success.main;
    const ratingBarWidth = `${Math.min(100, Math.max(12, (d.rating / 2) * 100))}%`;

    return (
        <TooltipShell sx={{ minWidth: "15rem", padding: 0, overflow: "hidden" }}>
            <ProfileMatchTooltipHeader
                timestamp={d.timestamp}
                won={d.won}
                myScore={d.myScore}
                oppScore={d.oppScore}
            />

            <Box
                sx={{
                    ...TOOLTIP_ROW_SX,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.55rem",
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        color: colors.text.secondary,
                        lineHeight: 1,
                    }}
                >
                    Rating 3.0
                </Typography>

                <Box
                    sx={{
                        ml: "auto",
                        position: "relative",
                        minWidth: "3.1rem",
                        px: "0.55rem",
                        py: "0.3rem",
                        borderRadius: "8px",
                        bgcolor: alpha(ratingColor, 0.12),
                        overflow: "hidden",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.82rem",
                            fontWeight: 700,
                            color: ratingColor,
                            lineHeight: 1.2,
                            fontVariantNumeric: "tabular-nums",
                            textAlign: "center",
                        }}
                    >
                        {d.rating.toFixed(2)}
                    </Typography>
                    <Box
                        sx={{
                            position: "absolute",
                            left: 0,
                            bottom: 0,
                            height: "2px",
                            width: ratingBarWidth,
                            bgcolor: ratingColor,
                        }}
                    />
                </Box>
            </Box>
        </TooltipShell>
    );
}

function ChartYAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-30} y={-8} width={26} height={16}>
                <Typography component="div" sx={{ fontSize: "0.62rem", fontWeight: 500, color: colors.text.muted, textAlign: "right", lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

const AXIS_TICK_TYPOGRAPHY_SX = {
    fontSize: "0.68rem",
    fontWeight: 500,
    color: colors.text.muted,
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
};

function ChartXAxisTick({ x, y, payload }) {
    return (
        <g transform={`translate(${x},${y})`}>
            <foreignObject x={-24} y={4} width={48} height={18}>
                <Typography component="div" sx={{ ...AXIS_TICK_TYPOGRAPHY_SX, textAlign: "center" }}>
                    {payload.value}
                </Typography>
            </foreignObject>
        </g>
    );
}

export default function OverviewRatingTrendChart({
    last25MatchData = {},
    maxMatches = 20,
    isLoading = false,
    showFooterStats = true,
}) {
    const { points } = useMemo(
        () => buildRating3TrendSeries(last25MatchData, maxMatches),
        [last25MatchData, maxMatches]
    );

    const avgRating = useMemo(() => {
        if (!points.length) return null;
        return Math.round((points.reduce((s, p) => s + p.rating, 0) / points.length) * 100) / 100;
    }, [points]);

    const yDomain = useMemo(() => {
        if (!points.length) return [0, 2];
        const vals = points.map((p) => p.rating);
        const min = Math.min(...vals);
        const max = Math.max(...vals);
        const pad = Math.max(0.15, (max - min) * 0.15);
        return [
            Math.round((min - pad) * 10) / 10,
            Math.round((max + pad) * 10) / 10,
        ];
    }, [points]);

    const gameAxisTicks = useMemo(
        () => buildGameAxisTicks(points.length),
        [points.length],
    );

    const ratingStats = useMemo(
        () => buildRatingTrendStats(avgRating, points),
        [avgRating, points]
    );

    if (isLoading) {
        return (
            <ProfileChartSkeleton
                height="11rem"
                statCount={showFooterStats ? 3 : 0}
            />
        );
    }

    if (points.length < 2) {
        return null;
    }

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            <Box sx={{ width: "100%", height: "11rem", minWidth: 0 }}>
                <ClientResponsiveContainer>
                    <LineChart data={points} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                        <CartesianGrid
                            vertical={false}
                            stroke={alpha(colors.text.muted, 0.08)}
                            strokeDasharray=""
                        />
                        <XAxis
                            dataKey="gameNumber"
                            type="number"
                            domain={[1, Math.max(points.length, 1)]}
                            ticks={gameAxisTicks}
                            tick={<ChartXAxisTick />}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                        />
                        <YAxis
                            domain={yDomain}
                            tick={<ChartYAxisTick />}
                            axisLine={false}
                            tickLine={false}
                            width={36}
                            tickCount={5}
                        />
                        <ReferenceLine
                            y={1.0}
                            stroke={alpha(colors.text.muted, 0.3)}
                            strokeDasharray="4 4"
                            strokeWidth={1}
                        />
                        {avgRating && (
                            <ReferenceLine
                                y={avgRating}
                                stroke={alpha(colors.primary.main, 0.35)}
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                        )}
                        <Tooltip
                            content={<RatingTooltip />}
                            cursor={{
                                stroke: alpha(colors.primary.main, 0.2),
                                strokeWidth: 1,
                                strokeDasharray: "4 4",
                            }}
                        />
                        <Line
                            type="linear"
                            dataKey="rating"
                            stroke={colors.primary.main}
                            strokeWidth={1.5}
                            dot={false}
                            activeDot={{
                                r: 5,
                                fill: colors.primary.main,
                                stroke: alpha(colors.primary.main, 0.4),
                                strokeWidth: 2,
                            }}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ClientResponsiveContainer>
            </Box>

            {showFooterStats ? (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "stretch",
                        gap: "0.75rem",
                        mt: "1.15rem",
                        // Keep hover lift from clipping under the card edge
                        mb: "0.15rem",
                        pb: "0.2rem",
                    }}
                >
                    {ratingStats.map((stat) => (
                        <Box
                            key={stat.label}
                            sx={{
                                position: "relative",
                                flex: 1,
                                overflow: "hidden",
                                borderRadius: "14px",
                                px: { xs: "0.65rem", md: "1rem" },
                                py: { xs: "0.7rem", md: "0.85rem" },
                                background: `
                                linear-gradient(
                                    180deg,
                                    rgba(255,255,255,0.025) 0%,
                                    rgba(255,255,255,0.01) 100%
                                )
                            `,
                                border: "1px solid rgba(255,255,255,0.05)",
                                backdropFilter: "blur(10px)",
                                boxShadow: `
                                inset 0 1px 0 rgba(255,255,255,0.04),
                                0 10px 24px rgba(0,0,0,0.18)
                            `,
                                transition: "all 240ms cubic-bezier(0.22,1,0.36,1)",
                                "&:hover": {
                                    borderColor: alpha(stat.color, 0.22),
                                    boxShadow: `
                                    inset 0 1px 0 rgba(255,255,255,0.05),
                                    0 12px 28px rgba(0,0,0,0.24),
                                    0 0 24px ${stat.glow}
                                `,
                                },
                                "&::before": {
                                    content: '""',
                                    position: "absolute",
                                    top: 0,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "42%",
                                    height: "1px",
                                    background: `
                                    linear-gradient(
                                        90deg,
                                        transparent,
                                        ${alpha(stat.color, 0.95)},
                                        transparent
                                    )
                                `,
                                    boxShadow: `
                                    0 0 10px ${alpha(stat.color, 0.45)}
                                `,
                                },
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    inset: 0,
                                    background: `
                                    radial-gradient(
                                        circle at bottom,
                                        ${alpha(stat.color, 0.08)} 0%,
                                        transparent 70%
                                    )
                                `,
                                    opacity: 0.8,
                                    pointerEvents: "none",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    fontSize: "0.62rem",
                                    fontWeight: 700,
                                    color: colors.text.muted,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.14em",
                                    mb: "0.45rem",
                                }}
                            >
                                {stat.label}
                            </Typography>

                            <Typography
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    fontSize: "1.05rem",
                                    fontWeight: 700,
                                    color: stat.color,
                                    lineHeight: 1,
                                    letterSpacing: "-0.03em",
                                    fontVariantNumeric: "tabular-nums",
                                    textShadow: `0 0 14px ${alpha(stat.color, 0.18)}`,
                                }}
                            >
                                {stat.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}

OverviewRatingTrendChart.propTypes = {
    last25MatchData: PropTypes.object,
    maxMatches: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    isLoading: PropTypes.bool,
    showFooterStats: PropTypes.bool,
};
